import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";

import { getApi } from "../../api/api";
import {
  ClusterInterface,
  PublicStoreInterface,
} from "../../common/types/interfaces";
import {
  selectClusterList,
  selectClusterListLoading,
} from "../../store/slices/clusterListSlice";
import { useCityId } from "../../common/hooks/useCityId";

export interface StoreListItem {
  store: PublicStoreInterface;
  cluster: ClusterInterface | null;
}

export const useStore = () => {
  const [searchParams] = useSearchParams();
  const clusterParam = searchParams.get("cluster");
  const search = searchParams.get("search") || "";
  const clusterId = clusterParam ? Number(clusterParam) : undefined;

  const clusters = useSelector(selectClusterList);
  const clustersLoading = useSelector(selectClusterListLoading);

     const cityId = useCityId()

  const [stores, setStores] = useState<PublicStoreInterface[]>([]);
  const [loading, setLoading] = useState(true);
  const [isFetching, setIsFetching] = useState(false);

  useEffect(() => {
    if (clustersLoading) return;

    let cancelled = false;
    setIsFetching(true);

    getApi()
      .getStores({
        target: "logmall",
        clusterId,
        search: search || undefined,
        cityId,
      })
      .then((data) => {
        if (!cancelled) {
          setStores(data);
          setLoading(false);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setStores([]);
          setLoading(false);
        }
      })
      .finally(() => {
        if (!cancelled) setIsFetching(false);
      });

    return () => {
      cancelled = true;
    };
  }, [clusterId, search, cityId, clustersLoading]);

  const cluster = clusterId
    ? clusters.find((c) => c.id === clusterId) || null
    : null;

  const items: StoreListItem[] = useMemo(
    () =>
      stores.map((s) => ({
        store: s,
        cluster: clusters.find((c) => c.id === s.clusterId) || null,
      })),
    [stores, clusters],
  );

  return {
    items,
    cluster,
    count: items.length,
    ready: !clustersLoading && !loading,
    isFetching,
  };
};
